import { Entity } from '@backstage/catalog-model';
import { AppExtensionManifest, ExtensionLifecycle } from '../types';

const VALID_LIFECYCLES: ExtensionLifecycle[] = [
  'experimental',
  'production',
  'deprecated',
];

function isValidManifestSpec(spec: unknown): spec is Record<string, unknown> {
  if (!spec || typeof spec !== 'object') {
    return false;
  }
  const s = spec as Record<string, unknown>;
  return (
    typeof s.extensionId === 'string' &&
    typeof s.displayName === 'string' &&
    typeof s.type === 'string' &&
    typeof s.frameworkVersion === 'string' &&
    Array.isArray(s.hooks)
  );
}

// Same convention as dora-scorecard-backend's GitHubCollector: the standard
// github.com/project-slug annotation, falling back to parsing
// backstage.io/source-location if it's ever missing.
function extractGithubRepoSlug(entity: Entity): string | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const slug = annotations['github.com/project-slug'];
  if (slug) {
    return slug;
  }
  const sourceLocation = annotations['backstage.io/source-location'];
  const match = sourceLocation?.match(/github\.com\/([^/]+\/[^/]+)/);
  return match?.[1];
}

/**
 * Maps a catalog AppExtension entity into the shape this plugin's UI
 * expects. previewImageUrl is left exactly as declared in spec — resolving
 * it to actual image bytes is a separate client-side GitHub fetch (see
 * GithubPreviewImageClient), not something this mapping does.
 */
export function entityToManifest(
  entity: Entity,
): AppExtensionManifest | undefined {
  if (!isValidManifestSpec(entity.spec)) {
    return undefined;
  }
  const spec = entity.spec;
  const sourceRepository = extractGithubRepoSlug(entity) ?? 'unknown/unknown';
  const lifecycle = VALID_LIFECYCLES.includes(
    spec.lifecycle as ExtensionLifecycle,
  )
    ? (spec.lifecycle as ExtensionLifecycle)
    : undefined;

  return {
    ...spec,
    description: spec.description ?? entity.metadata.description,
    owner: typeof spec.owner === 'string' ? spec.owner : undefined,
    lifecycle,
    tags: entity.metadata.tags,
    sourceRepository,
    sourcePath: 'catalog-info.yaml',
  } as AppExtensionManifest;
}
